import { DairyCowType, NullableRangeObject } from "models";

export interface EmissionsFactorRefrigerant {
  id: string;
  name: string;
  emissionFactorKg: number;
  enabled: boolean;
}

export const FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION = {
  PRODUCED: "produced",
  DELIVERED: "delivered",
} as const;

export const FARM_FOOTPRINTING_DAIRY_MOORLAND = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION = {
  THIN: "thin",
  MODERATE: "moderate",
  FAT: "fat",
} as const;

export const FARM_FOOTPRINTING_DAIRY_CALVES_FEED = {
  MAT: "mat",
  WHOLE_MILK: "whole_milk",
} as const;

export const FARM_FOOTPRINTING_DAIRY_FEED_SOURCE_TYPE = {
  PURCHASED: "purchased",
  GROWN: "grown",
} as const;

export const FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE = {
  YES: "yes",
  NO: "no",
} as const;

export const FARM_FOOTPRINTING_OVERALL_SOIL_TYPE = {
  CLAY: "clay",
  SILT: "silt",
  SAND: "sand",
} as const;

export const FARM_FOOTPRINTING_HAS_LAND_CHANGED = {
  YES: "yesLUC",
  NO: "noLUC",
  UNKNOWN: "unknown",
} as const;

export const FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION = {
  UNKNOWN: "unknown",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
} as const;

export const FARM_FOOTPRINTING_DRYING_ENERGY_TYPE = {
  DIESEL: "diesel",
  PETROL: "petrol",
  NATURALGAS: "naturalGas",
  HEATINGOIL: "heatingOil",
  ELECTRICITY: "electricity",
  WOODCHIP_HEATER: "woodchip_heater",
} as const;

export const FERTILIZER_UNITS = {
  TONNES: "tonnes",
  KILOGRAMS: "kilograms",
  CUBIC_METERS: "cubic_meters",
  LITERS: "liters",
} as const;

export const PLANT_PROTECTION_UNITS = {
  KILOGRAMS: "kilograms",
  LITERS: "liters",
} as const;

export const DAIRY_FEED_ADDITIVE_UNITS = {
  KILOGRAMS: "kilograms",
  LITERS: "liters",
  CUBIC_METERS: "cubic_meters",
  TONNES: "tonnes",
} as const;

export const DAIRY_MANURE_ADDITIVE_UNITS = {
  KILOGRAMS: "kilograms",
  LITERS: "liters",
} as const;

export const DAIRY_FEED_ENERGY_UNIT = {
  MJNEL: "mj_nel",
  MJME: "mj_me",
} as const;

export const DAIRY_FEED_PRODUCTION_UNITS = {
  KILOGRAMS: "kilograms",
  LITERS: "liters",
  CUBIC_METERS: "cubic_meters",
  TONNES: "tonnes",
} as const;

export const FARM_FOOTPRINTING_DRYING_ENERGY_TYPE_SUFFIXES = {
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.DIESEL]: "L",
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.PETROL]: "L",
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.NATURALGAS]: "m3/t",
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.HEATINGOIL]: "L",
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.ELECTRICITY]: "kWh",
  [FARM_FOOTPRINTING_DRYING_ENERGY_TYPE.WOODCHIP_HEATER]: "kg",
} as const;

export const FOOTPRINTING_BANNER_KEYS = {
  FIRST: "first",
  MANY: "many",
  ALL: "all",
  UNSUBMITTED_ASSESSMENTS: "unsubmitted-assessments",
  SUBMITTED_ASSESSMENTS: "submitted-assessments",
} as const;

export interface AppliedPracticeBase {
  applicationId: string | null;
}

export type AppliedPractice<T> = AppliedPracticeBase & T;

export type ENUM_FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION =
  (typeof FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION)[keyof typeof FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION];

export type ENUM_FARM_FOOTPRINTING_DAIRY_MOORLAND = (typeof FARM_FOOTPRINTING_DAIRY_MOORLAND)[keyof typeof FARM_FOOTPRINTING_DAIRY_MOORLAND];
export type ENUM_FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE =
  (typeof FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE)[keyof typeof FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE];

export type ENUM_FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT =
  (typeof FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT)[keyof typeof FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT];

export type ENUM_FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE =
  (typeof FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE)[keyof typeof FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE];

export type ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION =
  (typeof FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION)[keyof typeof FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION];

export type ENUM_FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES = (typeof FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES)[keyof typeof FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES];

export type ENUM_FARM_FOOTPRINTING_DAIRY_CALVES_FEED = (typeof FARM_FOOTPRINTING_DAIRY_CALVES_FEED)[keyof typeof FARM_FOOTPRINTING_DAIRY_CALVES_FEED];

export type ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE =
  (typeof FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE)[keyof typeof FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE];

export type ENUM_FARM_FOOTPRINTING_DAIRY_FEED_SOURCE_TYPE =
  (typeof FARM_FOOTPRINTING_DAIRY_FEED_SOURCE_TYPE)[keyof typeof FARM_FOOTPRINTING_DAIRY_FEED_SOURCE_TYPE];

export type ENUM_FARM_FOOTPRINTING_OVERALL_SOIL_TYPE = (typeof FARM_FOOTPRINTING_OVERALL_SOIL_TYPE)[keyof typeof FARM_FOOTPRINTING_OVERALL_SOIL_TYPE];

export type ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED = (typeof FARM_FOOTPRINTING_HAS_LAND_CHANGED)[keyof typeof FARM_FOOTPRINTING_HAS_LAND_CHANGED];

export type ENUM_FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION =
  (typeof FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION)[keyof typeof FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION];

export type ENUM_FARM_FOOTPRINTING_DRYING_ENERGY_TYPE = (typeof FARM_FOOTPRINTING_DRYING_ENERGY_TYPE)[keyof typeof FARM_FOOTPRINTING_DRYING_ENERGY_TYPE];

export type ENUM_FERTILIZER_UNITS = (typeof FERTILIZER_UNITS)[keyof typeof FERTILIZER_UNITS];
export type ENUM_PLANT_PROTECTION_UNITS = (typeof PLANT_PROTECTION_UNITS)[keyof typeof PLANT_PROTECTION_UNITS];
export type ENUM_DAIRY_FEED_ADDITIVE_UNITS = (typeof DAIRY_FEED_ADDITIVE_UNITS)[keyof typeof DAIRY_FEED_ADDITIVE_UNITS];
export type ENUM_DAIRY_MANURE_ADDITIVE_UNITS = (typeof DAIRY_MANURE_ADDITIVE_UNITS)[keyof typeof DAIRY_MANURE_ADDITIVE_UNITS];
export type ENUM_DAIRY_FEED_ENERGY_UNIT = (typeof DAIRY_FEED_ENERGY_UNIT)[keyof typeof DAIRY_FEED_ENERGY_UNIT];
export type ENUM_DAIRY_FEED_PRODUCTION_UNITS = (typeof DAIRY_FEED_PRODUCTION_UNITS)[keyof typeof DAIRY_FEED_PRODUCTION_UNITS];

export interface AggregatedFarmFootprint {
  id: string;
  year: number;
  isSubmitted: boolean;
  isVerified?: boolean;
  overallSoilType: ENUM_FARM_FOOTPRINTING_OVERALL_SOIL_TYPE | null;
  averageSoilSaturation: ENUM_FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION | null;
  electricityKwh: number | null;
  renewablesPct: number | null;
  wasteKgs: number | null;
  wasteWaterM3: number | null;
  dieselL: number | null;
  petrolL: number | null;
  naturalGasM3: number | null;
  heatingOilL: number | null;
  refrigerantsActive: boolean | null;
  refrigerantUsage: Array<{
    refrigerantId: string | null;
    refrigerantType: string | null;
    amountUsedKg: number | null;
  }>;
  purchasesActive: boolean | null;
  purchases: Array<{
    retailerName: string | null;
    retailerCountryCode: string | null;
    retailerPostCode: string | null;
    mineralFertilizerPct: number | null;
    organicFertilizerPct: number | null;
    plantProtectionPct: number | null;
    seedsPct: number | null;
  }>;
  landUseChangeUsage: Array<{
    id: string;
    isUnknown: boolean;
    hasLandChanged: ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED;
    areaConvertedHa: number | null;
    landUseChangeId: string | null;
    yearOfConversion: number | null;
    previousLandUseType: string | null;
  }>;
}

export interface AggregatedCropFootprint {
  id: string;
  isSubmitted: boolean;
  isVerified?: boolean;
  totalWeightTonsPerHa: number | null;
  preDryingMoistureContentPct: number | null;
  byproductPct: number | null;
  seedWeightKgPerHa: number | null;
  soilPh: number | null;
  fieldIds: string[];
  tillageActive: boolean | null;
  seedingActive: boolean | null;
  tillageUsage: Array<
    AppliedPractice<{
      implementedOnHa: number | null;
      tillageDepth: number | null;
    }>
  >;
  seedingUsage: Array<
    AppliedPractice<{
      implementedOnHa: number | null;
    }>
  >;
  mineralFertilizerActive: boolean | null;
  organicFertilizerActive: boolean | null;
  mineralFertilizerUsage: Array<
    AppliedPractice<{
      amount: number | null;
      amountUnit: ENUM_FERTILIZER_UNITS | null;
    }>
  >;
  organicFertilizerUsage: Array<
    AppliedPractice<{
      amount: number | null;
      amountUnit: ENUM_FERTILIZER_UNITS | null;
      nValue: number | null;
    }>
  >;
  limeT: number | null;
  plantProtectionActive: boolean | null;
  plantProtectionUsage: Array<
    AppliedPractice<{
      amount: number | null;
      amountUnit: ENUM_PLANT_PROTECTION_UNITS | null;
      activeIngredientsPct: number | null;
    }>
  >;
  irrigationActive: boolean | null;
  waterUsedM3: number | null;
  dryingActive: boolean | null;
  energyType: ENUM_FARM_FOOTPRINTING_DRYING_ENERGY_TYPE | null;
  fuelAmount: number | null;
  renewableEnergyPct: number | null;
  postDryingMoistureContentPct: number | null;
  driedWeightTonsPerHa: number | null;
  cropSaleActive: boolean | null;
  cropSales: Array<{
    purchaserName: string | null;
    purchaserCountryCode: string | null;
    purchaserPostCode: string | null;
    soldYieldPct: number | null;
  }>;
  reviewFieldIds?: string[];
}

export interface AggregatedDairyFootprint {
  id: string;
  year: number;
  farmId: string;
  isSubmitted: boolean;
  milkProduction: ENUM_FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION | null;
  milkYieldKg: number | null;
  fatContentPct: number | null;
  proteinContentPct: number | null;
  cowsInPastureActive: ENUM_FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE | null;
  cowsInPasture: number | null;
  daysInPasturePerYear: number | null;
  avgHoursInPasturePerDay: number | null;
  forageLossPct: number | null;
  feedAdditiveActive: boolean | null;
  moorlandActive?: ENUM_FARM_FOOTPRINTING_DAIRY_MOORLAND | null;
  fodderProductionAreaHa?: number | null;
  annualWaterLevelCm?: number | null;
  amountStoredGasTankPct: number | null;
  herdDetails: {
    calvesAmount?: number | null;
    avgCowLosses?: number | null;
    avgCowUsefulLifeMonths?: number | null;
    cowWeightKnown?: ENUM_FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT | null;
    averageCowWeightKg?: number | null;
    estimatedCowBodyCondition?: ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION | null;
    selectedCalvesFeed?: ENUM_FARM_FOOTPRINTING_DAIRY_CALVES_FEED | null;
    ageOfFirstCalvingMonths?: number | null;
    timeBetweenCalvingDays?: number | null;
  };
  herdSize: Array<{
    cowTypeId: string | null;
    cowCount: number | null;
  }>;
  cowSales: {
    cowSalesInPoorCondition: number | null;
    cowSalesForLightManufacturing: number | null;
    cowSalesForStandardManufacturing: number | null;
    cowSalesInGoodCondition: number | null;
    cowSalesInPremiumCondition: number | null;
  };
  feedAdditiveUsage: Array<{
    additiveType: string | null;
    amount: number | null;
    amountUnit: ENUM_DAIRY_FEED_ADDITIVE_UNITS | null;
  }>;
  feedInformation: Array<{
    id: string;
    feedTypeId?: string;
    totalAmountFedKg?: number | null;
    maizeSilagePct?: number | null;
    grassSilagePct?: number | null;
    energyValue?: number | null;
    energyUnit: ENUM_DAIRY_FEED_ENERGY_UNIT | null;
    dryMatterPct?: number | null;
    emissionFactorKgCo2Eq?: number | null;
    feedType?: DairyFeedTypes;
    feedProduction?: {
      cropUsedForFeed: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
      cropYieldTPerHa: number | null;
      dieselConsumptionL: number | null;
      energyUsedKwh: number | null;
      mineralNitrogenKgHa: number | null;
      mineralPhosphorusKgHa: number | null;
      mineralPotassiumKgHa: number | null;
      nitrogenOrganicFertKgHa: number | null;
      totalLimeAppliedKgCaCo3PerHa: number | null;
      totalPlantProtectionApplied: number | null;
      totalPlantProtectionAppliedUnit: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
      totalWaterUsedIrregationLPerHa: number | null;
      soldOffFarmPct: number | null;
      fedToCowsPct: number | null;
      otherUsesPct: number | null;
    } | null;
  }>;
  manureStorageUsage: Array<{
    storageType: string | null;
    amountStoredPct: number | null;
  }>;
}

export type FootprintingFormCompletionStats = {
  completionPct: number;
  pagesCompleted: number[];
  pagesIncomplete: number[];
  completionPayout?: number;
};

export type FarmFootprintReturnBody = FootprintingFormCompletionStats & {
  footprint?: AggregatedFarmFootprint;
  year: number;
  farmId: string;
};

export type CropFootprintReturnBody = FootprintingFormCompletionStats & {
  footprint?: AggregatedCropFootprint;
  cropId: string;
  year: number;
  farmId: string;
};

export type DairyFootprintReturnBody = FootprintingFormCompletionStats & {
  footprint?: AggregatedDairyFootprint;
  year: number;
  feedProductionCompletion?: Record<string, boolean>;
};

export interface FootprintingFarmPrimaryData {
  id: string;
  isSubmitted?: boolean;
  isVerified?: boolean;
  farmId: string;
  year: number;
  overallSoilType?: ENUM_FARM_FOOTPRINTING_OVERALL_SOIL_TYPE | null;
  averageSoilSaturation?: ENUM_FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION | null;
  refrigerantsActive?: boolean | null;
  purchasesActive?: boolean | null;
}

export interface RetailerData {
  id?: string;
  name: string | null;
  postcode: string | null;
  countryCode: string | null;
}

export interface RetailerPurchase {
  id?: string;
  retailerId?: string;
  retailer: RetailerData;
  mineralFertilizerPct: number | null;
  organicFertilizerPct: number | null;
  seedsPct: number | null;
  plantProtectionPct: number | null;
  cowFeedPct: number | null;
}

export interface FootprintingFarmRetailerPayload {
  footprintingFarmPrimaryDataId: string;
  farmId: string;
  purchases: RetailerPurchase[];
}

export interface FootprintingFarmResourceConsumption {
  id?: string;
  footprintingFarmPrimaryDataId: string;
  electricityKwh?: number | null;
  renewablesPct?: number | null;
  dairyElectricityPct?: number | null;
  wasteKgs?: number | null;
  wasteWaterM3?: number | null;
  dieselL?: number | null;
  petrolL?: number | null;
  naturalGasM3?: number | null;
  heatingOilL?: number | null;
}

export interface UploadableRefrigerant {
  id?: string;
  emissionFactorRefrigerantId: string;
  amountKg: number | null;
  usedForDairy: boolean;
}

export interface FootprintingFarmRefrigerantPayload {
  footprintingFarmPrimaryDataId: string;
  selectedRefrigerants: UploadableRefrigerant[];
}

export interface FootprintingFarmRefrigerantUsage {
  id?: string;
  footprintingFarmPrimaryDataId: string;
  emissionFactorRefrigerantId: string;
  usedForDairy: boolean;
  amountKg: number | null;
}

export interface UploadableLandUseChange {
  id?: string;
  hasLandChanged: ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED | null;
  landUseChangeId?: string;
  previousLandUseChange?: {
    id?: string;
    translationSlug: string;
  };
  yearOfConversion?: number | null;
  areaConvertedHa?: number | null;
  isUnknown?: boolean;
}

export interface LandUseChangeRequestPayload {
  farmId: string;
  selectedLandUseChange: UploadableLandUseChange[];
}

export interface FarmLandUseChange {
  id?: string;
  farmId: string;
  landUseChangeId?: string;
  hasLandChanged: ENUM_FARM_FOOTPRINTING_HAS_LAND_CHANGED | null;
  yearOfConversion: number | null;
  areaConvertedHa: number | null;
  isUnknown: boolean;
  previousLandUseChange?: {
    id: string;
    translationSlug: string;
  };
}

export interface UploadablePlantProtection {
  id?: string;
  practicesPlantProtectionId: string;
  amount: number | null;
  amountUnit: ENUM_PLANT_PROTECTION_UNITS | null;
}

export interface FootprintingFarmCropPlantProtectionPayload {
  footprintingFarmPrimaryDataId: string;
  selectedPlantProtection: UploadablePlantProtection[];
}

export interface FootprintingCropPrimaryData {
  id: string;
  isSubmitted?: boolean;
  isVerified?: boolean;
  farmId: string;
  cropId: string;
  year: number;
  totalWeightTonsPerHa?: number | null;
  preDryingMoistureContentPct?: number | null;
  byproductPct?: number | null;
  seedWeightKgPerHa?: number | null;
  soilPh?: number | null;
  waterUsedM3?: number | null;
  fieldIds?: string[];
  energyType?: ENUM_FARM_FOOTPRINTING_DRYING_ENERGY_TYPE | null;
  fuelAmount?: number | null;
  renewableEnergyPct?: number | null;
  postDryingMoistureContentPct?: number | null;
  driedWeightTonsPerHa?: number | null;
  limeT?: number | null;
  mineralFertilizerActive?: boolean | null;
  organicFertilizerActive?: boolean | null;
  plantProtectionActive?: boolean | null;
  tillageActive?: boolean | null;
  seedingActive?: boolean | null;
  irrigationActive?: boolean | null;
  dryingActive?: boolean | null;
  cropSaleActive?: boolean | null;
  reviewFieldIds?: string[];
}

export interface FootprintingCropSeeding {
  id?: string;
  footprintingFarmCropPrimaryDataId: string;
  practicesSeedingId: string;
  implementedOnHa: number | null;
}

export interface FootprintingCropTillage {
  id?: string;
  footprintingFarmCropPrimaryDataId: string;
  practicesTillageId: string;
  implementedOnHa: number | null;
  tillageDepth: number | null;
}

export interface FootprintingCropFertilizer {
  id?: string;
  fertilizerId: string;
  amount: number | null;
  amountUnit: ENUM_FERTILIZER_UNITS | null;
  nValue?: number | null;
}

export interface PurchaserData {
  id?: string;
  farmId: string;
  name: string | null;
  postCode: string | null;
  countryCode: string | null;
}
export interface CropSaleData {
  id?: string;
  purchaserId?: string;
  purchaser: PurchaserData;
  soldYieldPct: number | null;
}

export interface FootprintingCropSalesPayload {
  footprintingFarmCropPrimaryDataId: string;
  farmId: string;
  cropSales: CropSaleData[];
}

export interface FootprintingDairyPrimaryData {
  id: string;
  farmId: string;
  year: number;
  isSubmitted?: boolean;
  isVerified?: boolean;
  milkProduction?: ENUM_FARM_FOOTPRINTING_DAIRY_MILK_PRODUCTION | null;
  milkYieldKg?: number | null;
  fatContentPct?: number | null;
  proteinContentPct?: number | null;
  feedAdditiveActive?: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES | null;
  cowsInPastureActive?: ENUM_FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE | null;
  cowsInPasture?: number | null;
  daysInPasturePerYear?: number | null;
  avgHoursInPasturePerDay?: number | null;
  forageLossPct?: number | null;
  moorlandActive?: ENUM_FARM_FOOTPRINTING_DAIRY_MOORLAND | null;
  fodderProductionAreaHa?: number | null;
  annualWaterLevelCm?: number | null;
  amountStoredGasTankPct?: number | null;
  manureAdditiveActive?: ENUM_FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE | null;
}

export interface FootprintingDairyHerdDetails {
  id: string;
  calvesAmount?: number | null;
  avgCowLosses?: number | null;
  avgCowUsefulLifeMonths?: number | null;
  cowWeightKnown?: ENUM_FARM_FOOTPRINTING_DAIRY_KNOWN_COW_WEIGHT | null;
  averageCowWeightKg?: number | null;
  estimatedCowBodyCondition?: ENUM_FARM_FOOTPRINTING_DAIRY_COW_BODY_CONDITION | null;
  selectedCalvesFeed?: ENUM_FARM_FOOTPRINTING_DAIRY_CALVES_FEED | null;
  ageOfFirstCalvingMonths?: number | null;
  timeBetweenCalvingDays?: number | null;
}

export interface FootprintingDairyHerdSize {
  id: string;
  primaryDataId: string;
  cowTypeId: string;
  cowCount: number;
  cowType?: DairyCowType;
}

export interface UploadableHerdSize {
  id?: string;
  cowTypeId: string;
  cowCount: number;
}

export interface UploadableFeedAdditive {
  id?: string;
  feedAdditiveId: string;
  amount: number | null;
  amountUnit: ENUM_DAIRY_FEED_ADDITIVE_UNITS | null;
}

export interface FootprintingDairyFeedAdditivePayload {
  footprintingFarmPrimaryDataId: string;
  selectedFeedAdditive: UploadableFeedAdditive[];
}

export interface UploadableManureStorage {
  id?: string;
  manureStorageId: string;
  amountStoredPct: number | null;
}

export interface FootprintingDairyManureStoragePayload {
  footprintingFarmPrimaryDataId: string;
  selectedManureStorage: UploadableManureStorage[];
}

export interface UploadableManureAdditive {
  id?: string;
  manureAdditiveId: string;
  amountUsedM3: number | null;
  amountUsedM3Unit: ENUM_DAIRY_MANURE_ADDITIVE_UNITS | null;
  enrichedSlurryPct: number | null;
}

export interface FootprintingDairyManureAdditivePayload {
  footprintingFarmPrimaryDataId: string;
  selectedManureAdditive: UploadableManureAdditive[];
}

export interface FootprintingDairyCowSales {
  id: string;
  primaryDataId: string;
  farmId: string;
  cowSalesInPoorCondition: number | null;
  cowSalesForLightManufacturing: number | null;
  cowSalesForStandardManufacturing: number | null;
  cowSalesInGoodCondition: number | null;
  cowSalesInPremiumCondition: number | null;
}

export interface UploadableCowSales {
  id?: string;
  cowSalesInPoorCondition: number | null;
  cowSalesForLightManufacturing: number | null;
  cowSalesForStandardManufacturing: number | null;
  cowSalesInGoodCondition: number | null;
  cowSalesInPremiumCondition: number | null;
}

export interface FootprintingDairyFeedInformation {
  id: string;
  primaryDataId: string;
  farmId: string;
  feedTypeId?: string;
  totalAmountFedKg?: number | null;
  maizeSilagePct?: number | null;
  grassSilagePct?: number | null;
  energyValue?: number | null;
  energyUnit: ENUM_DAIRY_FEED_ENERGY_UNIT | null;
  dryMatterPct?: number | null;
  cowsPct?: number | null;
  calvesPct?: number | null;
  emissionFactorKgCo2Eq?: number | null;
  feedType?: DairyFeedTypes;
}

export interface UploadableFeedInformation {
  id?: string;
  feedTypeId?: string;
  totalAmountFedKg?: number | null;
  maizeSilagePct?: number | null;
  grassSilagePct?: number | null;
  energyValue?: number | null;
  energyUnit?: ENUM_DAIRY_FEED_ENERGY_UNIT | null;
  dryMatterPct?: number | null;
  cowsPct?: number | null;
  calvesPct?: number | null;
  emissionFactorKgCo2Eq?: number | null;
}

export interface DairyFeedTypes {
  id: string;
  translationSlug: string;
  sourceType: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_SOURCE_TYPE;
  energyMjMeKgDm: number | null;
  energyMjNelKgDm: number | null;
  dryMatterPerFeedTypePct: number | null;
  enabled: boolean;
}

export interface DairyFeedProduction {
  id?: string;
  feedInformationId: string;
  primaryDataId?: string;
  cropUsedForFeed?: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
  cropYieldTPerHa?: number | null;
  dieselConsumptionL?: number | null;
  energyUsedKwh?: number | null;
  mineralNitrogenKgHa?: number | null;
  mineralPhosphorusKgHa?: number | null;
  mineralPotassiumKgHa?: number | null;
  nitrogenOrganicFertKgHa?: number | null;
  totalLimeAppliedKgCaCo3PerHa?: number | null;
  totalPlantProtectionApplied?: number | null;
  totalPlantProtectionAppliedUnit?: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
  totalWaterUsedIrregationLPerHa?: number | null;
  soldOffFarmPct?: number | null;
  fedToCowsPct?: number | null;
  otherUsesPct?: number | null;
}

// New interfaces for the calculated footprint response structure
export interface CalculatedFarmFootprintResponse {
  farmId: string;
  year: number;
  calculatedFootprint: {
    energy: {
      dieselFootprint: FootprintingFootprintAggregation;
      benzinFootprint: FootprintingFootprintAggregation;
      naturalGasFootprint: FootprintingFootprintAggregation;
      heatingOilFootprint: FootprintingFootprintAggregation;
      electricityFootprint: FootprintingFootprintAggregation;
      renewableElectricityFootprint: FootprintingFootprintAggregation;
    };
    water: {
      wastewaterFootprint: FootprintingFootprintAggregation;
      irrigationFootprint: FootprintingFootprintAggregation;
    };
    misc: {
      refrigerantFootprint: Array<{
        id: string;
        emissions: FootprintingFootprintAggregation;
      }>;
      wasteFootprint: FootprintingFootprintAggregation;
      landUseChangeFooprint: Array<{
        id: string;
        footprints: {
          biomassEmissionFootprint: FootprintingFootprintAggregation;
          deadOrganicMatterFootprint: FootprintingFootprintAggregation;
          socChangeFootprint: FootprintingFootprintAggregation;
        };
      }>;
    };
  };
}

// Interfaces for crop footprint application arrays
export interface CropSeedingApplicationFootprint {
  seedingId: string;
  footprint: FootprintingFootprintAggregation;
}

export interface CropTillageApplicationFootprint {
  tillageId: string;
  footprint: FootprintingFootprintAggregation;
}

export interface CropFertilizerApplicationFootprint {
  fertilizerApplicationId: string;
  footprint: FootprintingFootprintAggregation;
}

export interface CropPlantProtectionApplicationFootprint {
  plantProtectionApplicationId: string;
  footprint: FootprintingFootprintAggregation;
}

export interface CropFertilizerFootprint {
  id: string;
  emissions: FootprintingFootprintAggregation;
}

export interface CropPlantProtectionFootprint {
  id: string;
  emissions: FootprintingFootprintAggregation;
}

export interface CropAgriculturalLimeFootprint {
  emissions: FootprintingFootprintAggregation;
}

export interface CropRetailerTransportEmissions {
  retailerId: string;
  footprint: FootprintingFootprintAggregation;
}

export interface CalculatedCropFootprintResponse {
  farmId: string;
  cropId: string;
  year: number;
  calculatedFootprint: {
    dieselFootprint: {
      harvestDieselUsage: {
        cropId: string;
        footprint: FootprintingFootprintAggregation;
      };
      tillageApplication: CropTillageApplicationFootprint[];
      seedingApplication: CropSeedingApplicationFootprint[];
      fertilizerApplication: CropFertilizerApplicationFootprint[];
      plantProtectionApplications: CropPlantProtectionApplicationFootprint[];
    };
    dryingFootprint: {
      renewableElectricity: FootprintingFootprintAggregation;
      electricity: FootprintingFootprintAggregation;
      diesel: FootprintingFootprintAggregation;
      naturalGas: FootprintingFootprintAggregation;
      heatingOil: FootprintingFootprintAggregation;
      benzine: FootprintingFootprintAggregation;
      woodchipHeater: FootprintingFootprintAggregation;
    };
    upstreamTransportationFootprint: {
      organicFertilizers: CropRetailerTransportEmissions[];
      mineralFertilizers: CropRetailerTransportEmissions[];
      plantProtection: CropRetailerTransportEmissions[];
      seeding: CropRetailerTransportEmissions[];
    };
    downstreamTransportationFootprint: {
      crop: Array<{
        purchaserId: string;
        footprint: FootprintingFootprintAggregation;
      }>;
    };
    mineralFertilizerFootprint: {
      directN2O: CropFertilizerFootprint[];
      directCO2Eq: CropFertilizerFootprint[];
      NH3VolatilizationN2O: CropFertilizerFootprint[];
      NH3VolatilizationCO2Eq: CropFertilizerFootprint[];
      leachedNitrateN2O: CropFertilizerFootprint[];
      leachedNitrateCO2Eq: CropFertilizerFootprint[];
      productionCO2Eq: CropFertilizerFootprint[];
      totalNeutralisation: CropFertilizerFootprint[];
    };
    organicFertilizerFootprint: {
      organicFertilizerDirectN2O: CropFertilizerFootprint[];
      organicFertilizerDirectCO2Eq: CropFertilizerFootprint[];
      leachedNitrateN2O: CropFertilizerFootprint[];
      leachedNitrateCO2Eq: CropFertilizerFootprint[];
      NH3VolatilizationN2O: CropFertilizerFootprint[];
      NH3VolatilizationCO2Eq: CropFertilizerFootprint[];
    };
    plantProtectionFootprint: CropPlantProtectionFootprint[];
    agriculturalLimeFootprint: CropAgriculturalLimeFootprint;
    irrigationFootprint: FootprintingFootprintAggregation;
    cropResidueFootprint: {
      directN2O: FootprintingFootprintAggregation;
      directN2OCO2Eq: FootprintingFootprintAggregation;
      leachedNitrateN20: FootprintingFootprintAggregation;
      leachedNitrateCO2Eq: FootprintingFootprintAggregation;
    };
  };
}

export interface FootprintingFootprintAggregation {
  amount: {
    soc: NullableRangeObject;
    emission: NullableRangeObject;
  };
  penalty: {
    soc: NullableRangeObject;
    emission: NullableRangeObject;
  };
}

export interface FootprintingResultsTableRowData {
  totalCO2e: number;
  perHaCO2e: number;
  perKgYieldCO2e: number;
  shareOfTotal: number;
}

export type SubDataRowSource =
  | "mineralFertilizers"
  | "organicFertilizers"
  | "lime"
  | "upstreamTransportation"
  | "downstreamTransportation"
  | "harvesting"
  | "fertilizerApplication"
  | "plantProtectionApplications"
  | "seedingApplication"
  | "tillageApplication"
  | "diesel"
  | "dieselDrying"
  | "benzin"
  | "benzinDrying"
  | "naturalGas"
  | "naturalGasDrying"
  | "heating"
  | "heatingDrying"
  | "electricity"
  | "electricityDrying"
  | "renewableElectricityDrying"
  | "woodchipHeaterDrying"
  | "water";

export interface FootprintingResultsSubDataRowData extends FootprintingResultsTableRowData {
  source: SubDataRowSource;
}

export interface FootprintingResultsDataTableRow {
  data: FootprintingResultsTableRowData;
  subData: FootprintingResultsSubDataRowData[];
}

export type FootprintingCategory = keyof FootprintingResultsDataTable;

export interface FootprintingResultsDataTable {
  fertilizer: FootprintingResultsDataTableRow;
  plantProtection: FootprintingResultsDataTableRow;
  transportation: FootprintingResultsDataTableRow;
  fieldWork: FootprintingResultsDataTableRow;
  energy: FootprintingResultsDataTableRow;
  waste: FootprintingResultsDataTableRow;
  refrigerants: FootprintingResultsDataTableRow;
  residue: FootprintingResultsDataTableRow;
  luc: FootprintingResultsDataTableRow;
  other: FootprintingResultsDataTableRow;
}

export interface FootprintSummaryMetrics {
  totalCO2e: number;
  perHaCO2e: number;
  perKgYieldCO2e: number;
}

export interface FootprintCalculationSummary {
  overall: FootprintSummaryMetrics;
  tableData: FootprintingResultsDataTable;
}

export type FootprintingFarmFormType = "soil" | "electricity" | "waste" | "fuel" | "cooling" | "transport" | "landuse" | null;

export type FootprintingCropFormType =
  | "general"
  | "fields"
  | "tillageAndSeeding"
  | "fertilizers"
  | "plantProtection"
  | "irrigation"
  | "drying"
  | "transport"
  | null;

export interface CropAssessment {
  id: string;
  fieldIds: string[];
  isSubmitted: boolean;
}
